import React, { useCallback, useEffect, useState } from "react";
import Layout from "../../layout/Layout";
import { useAuth } from "../../../components/context/AuthContext";

import {
  getFreightCompanyList,
  createFreightRequest,
  getFreightRequestList,
  createFreightGoogleForm,
  syncFreightGoogleResponses,
  sendFreightRequestMail,
} from "../../../api/director/navlunApi";

import { formatDate, copyText } from "./navlunHelpers";

function DirectorNavlunRequestPage() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [requests, setRequests] = useState([]);

  const [requestForm, setRequestForm] = useState({
    year: new Date().getFullYear(),
    title: "",
    routeFrom: "",
    routeTo: "",
    loadType: "",
    quantity: "",
    quantityUnit: "ton",
    vehicleType: "",
    currency: "EUR",
    dueDate: "",
    description: "",
  });

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await getFreightCompanyList(1, 200);

      if (response?.data?.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      console.error("Firma listesi alınamadı:", error);
      alert("Firma listesi alınamadı.");
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getFreightRequestList(1, 100);

      if (response?.data?.success) {
        setRequests(response.data.data || []);
      }
    } catch (error) {
      console.error("Navlun teklif talepleri alınamadı:", error);
      alert("Navlun teklif talepleri alınamadı.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInitialData = useCallback(async () => {
    await Promise.all([fetchCompanies(), fetchRequests()]);
  }, [fetchCompanies, fetchRequests]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const getActiveMailCompanies = () => {
    return companies.filter((company) => {
      return (
        company &&
        company.isActive !== false &&
        company.mailPermission === true &&
        company.email
      );
    });
  };

  const getRequestMailRecipients = (requestItem) => {
    const selectedCompanies = Array.isArray(requestItem?.selectedCompanies)
      ? requestItem.selectedCompanies
      : [];

    return selectedCompanies.filter((company) => {
      return (
        company &&
        company.isActive !== false &&
        company.mailPermission === true &&
        company.email
      );
    });
  };

  const resetRequestForm = () => {
    setRequestForm({
      year: new Date().getFullYear(),
      title: "",
      routeFrom: "",
      routeTo: "",
      loadType: "",
      quantity: "",
      quantityUnit: "ton",
      vehicleType: "",
      currency: "EUR",
      dueDate: "",
      description: "",
    });
  };

  const handleRequestChange = (e) => {
    const { name, value } = e.target;

    setRequestForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();

    const activeMailCompanies = getActiveMailCompanies();

    if (!activeMailCompanies.length) {
      alert(
        "Teklif talebi oluşturmak için aktif ve mail izni olan firma bulunamadı."
      );
      return;
    }

    const ok = window.confirm(
      `Bu teklif talebi ${activeMailCompanies.length} aktif firmaya tanımlanacak.\n\n` +
        activeMailCompanies
          .map((company) => `${company.companyName} - ${company.email}`)
          .join("\n")
    );

    if (!ok) return;

    try {
      setLoading(true);

      const payload = {
        ...requestForm,
        year: Number(requestForm.year),
        quantity: Number(requestForm.quantity || 0),
        selectedCompanies: activeMailCompanies.map((company) => company._id),
      };

      const response = await createFreightRequest(payload);

      if (response?.data?.success) {
        alert(response.data.message || "Navlun teklif talebi oluşturuldu.");
        resetRequestForm();
        await fetchRequests();
      } else {
        alert(response?.data?.message || "İşlem başarısız.");
      }
    } catch (error) {
      console.error("Teklif talebi oluşturma hatası:", error);
      alert(
        error?.response?.data?.message ||
          "Teklif talebi oluşturulurken hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoogleForm = async (requestId) => {
    const ok = window.confirm(
      "Bu teklif talebi için Google Form oluşturulsun mu?"
    );

    if (!ok) return;

    try {
      setLoading(true);

      const response = await createFreightGoogleForm(requestId);

      if (response?.data?.success) {
        alert("Google Form başarıyla oluşturuldu.");
        await fetchRequests();
      } else {
        alert(response?.data?.message || "Google Form oluşturulamadı.");
      }
    } catch (error) {
      console.error("Google Form oluşturma hatası:", error);
      alert(
        error?.response?.data?.message ||
          "Google Form oluşturulurken hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSendMail = async (requestItem) => {
    const recipients = getRequestMailRecipients(requestItem);

    if (!requestItem.googleFormId || !requestItem.googleResponderUrl) {
      alert("Mail göndermeden önce Google Form oluşturulmalıdır.");
      return;
    }

    if (!recipients.length) {
      alert(
        "Bu teklif talebinde mail gönderilecek aktif ve izinli firma bulunamadı."
      );
      return;
    }

    const ok = window.confirm(
      `Aşağıdaki ${recipients.length} firmaya navlun teklif formu gönderilecek:\n\n` +
        recipients
          .map((company) => `${company.companyName} - ${company.email}`)
          .join("\n") +
        "\n\nFormda istenecek başlıca bilgiler:\n" +
        "- Navlun Bedeli\n" +
        "- Line\n" +
        "- Freetime\n" +
        "- Teslim TT / Transit Süresi\n\n" +
        "Devam edilsin mi?"
    );

    if (!ok) return;

    try {
      setLoading(true);

      const response = await sendFreightRequestMail(requestItem._id);

      if (response?.data?.success) {
        const summary = response.data.data?.summary || {};

        alert(
          `Mail gönderimi tamamlandı.\nToplam: ${
            summary.total || 0
          }\nGönderilen: ${summary.sent || 0}\nHatalı: ${summary.failed || 0}`
        );

        await fetchRequests();
      } else {
        alert(response?.data?.message || "Mail gönderilemedi.");
      }
    } catch (error) {
      console.error("Navlun mail gönderim hatası:", error);
      alert(
        error?.response?.data?.message || "Mail gönderilirken hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSyncResponses = async (requestId) => {
    try {
      setLoading(true);

      const response = await syncFreightGoogleResponses(requestId);

      if (response?.data?.success) {
        const data = response.data.data;

        alert(
          `Senkronizasyon tamamlandı.\nToplam: ${data.totalResponses}\nYeni: ${data.insertedCount}\nGüncellenen: ${data.updatedCount}\nEşleşmeyen: ${data.unmatchedCount}`
        );

        await fetchRequests();
      } else {
        alert(response?.data?.message || "Cevaplar senkronize edilemedi.");
      }
    } catch (error) {
      console.error("Google cevap sync hatası:", error);
      alert(
        error?.response?.data?.message ||
          "Cevaplar senkronize edilirken hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async (url) => {
    const copied = await copyText(url);

    alert(copied ? "Link kopyalandı." : "Link kopyalanamadı.");
  };

  const goDetail = (id) => {
    window.location.href = `/DirectorNavlunRequestDetail/${id}`;
  };

  const activeMailCompanyCount = getActiveMailCompanies().length;

  const totalRequestCount = requests.length;

  const formCreatedCount = requests.filter((item) => item.googleFormId).length;

  const totalResponseCount = requests.reduce((sum, item) => {
    return sum + Number(item.offerStats?.totalOfferCount || 0);
  }, 0);

  const unmatchedResponseCount = requests.reduce((sum, item) => {
    return sum + Number(item.offerStats?.unmatchedCount || 0);
  }, 0);

  if (!user) return null;

  return (
    <Layout>
      <div className="content-body" style={{ width: "100%", padding: "20px" }}>
        <div className="row mt-3 mb-4">
          <div className="col-lg-3 col-md-6 mb-3">
            <div
              className="card text-center h-100"
              style={{
                backgroundColor: "#fff3cd",
                borderRadius: "12px",
                border: "none",
              }}
            >
              <div className="card-body">
                <h6 style={{ color: "#856404", fontWeight: "600" }}>
                  TEKLİF TALEBİ
                </h6>
                <h2
                  style={{
                    color: "#856404",
                    fontSize: "2.2rem",
                    margin: "10px 0",
                  }}
                >
                  {totalRequestCount}
                </h2>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 mb-3">
            <div
              className="card text-center h-100"
              style={{
                backgroundColor: "#e8f4ff",
                borderRadius: "12px",
                border: "none",
              }}
            >
              <div className="card-body">
                <h6 style={{ color: "#0066cc", fontWeight: "600" }}>
                  FORM OLUŞAN
                </h6>
                <h2
                  style={{
                    color: "#0066cc",
                    fontSize: "2.2rem",
                    margin: "10px 0",
                  }}
                >
                  {formCreatedCount}
                </h2>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 mb-3">
            <div
              className="card text-center h-100"
              style={{
                backgroundColor: "#e6ffed",
                borderRadius: "12px",
                border: "none",
              }}
            >
              <div className="card-body">
                <h6 style={{ color: "#16803c", fontWeight: "600" }}>
                  GELEN CEVAP
                </h6>
                <h2
                  style={{
                    color: "#16803c",
                    fontSize: "2.2rem",
                    margin: "10px 0",
                  }}
                >
                  {totalResponseCount}
                </h2>
              </div>
            </div>
          </div>

          <div className="col-lg-3 col-md-6 mb-3">
            <div
              className="card text-center h-100"
              style={{
                backgroundColor: "#ffe6cc",
                borderRadius: "12px",
                border: "none",
              }}
            >
              <div className="card-body">
                <h6 style={{ color: "#ff6600", fontWeight: "600" }}>
                  EŞLEŞMEYEN
                </h6>
                <h2
                  style={{
                    color: "#ff6600",
                    fontSize: "2.2rem",
                    margin: "10px 0",
                  }}
                >
                  {unmatchedResponseCount}
                </h2>
              </div>
            </div>
          </div>
        </div>

        <section className="app-user-list">
          <div className="row">
            <div className="col-12">
              <div
                className="card"
                style={{
                  borderRadius: "12px",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                }}
              >
                <div className="card-header border-bottom p-4 d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="card-title m-0">Navlun Teklif Talepleri</h4>
                    <small className="text-muted">
                      Talep oluşturun, Google Form açın, mail gönderin ve her
                      talep için gelen cevap durumunu takip edin.
                    </small>
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={fetchInitialData}
                    disabled={loading}
                  >
                    Yenile
                  </button>
                </div>

                <div className="card-body pt-3">
                  {loading && (
                    <div className="alert alert-info py-2">
                      İşlem yapılıyor...
                    </div>
                  )}

                  <div className="row">
                    <div className="col-lg-4 mb-3">
                      <div className="card border">
                        <div className="card-header">
                          <h5 className="m-0">Teklif Talebi Oluştur</h5>
                        </div>

                        <div className="card-body">
                          <div className="alert alert-light border py-2">
                            Bu talep oluşturulurken aktif ve mail izni olan
                            firmalar otomatik eklenecek.
                            <br />
                            <strong>
                              Gönderim kapsamındaki firma sayısı:
                            </strong>{" "}
                            {activeMailCompanyCount}
                          </div>

                          <form onSubmit={handleRequestSubmit}>
                            <div className="row">
                              <div className="col-md-4 mb-2">
                                <label className="form-label">Yıl</label>
                                <input
                                  name="year"
                                  type="number"
                                  className="form-control"
                                  value={requestForm.year}
                                  onChange={handleRequestChange}
                                  required
                                />
                              </div>

                              <div className="col-md-8 mb-2">
                                <label className="form-label">
                                  Para Birimi
                                </label>
                                <select
                                  name="currency"
                                  className="form-control"
                                  value={requestForm.currency}
                                  onChange={handleRequestChange}
                                >
                                  <option value="EUR">EUR</option>
                                  <option value="USD">USD</option>
                                  <option value="TRY">TRY</option>
                                </select>
                              </div>
                            </div>

                            <div className="mb-2">
                              <label className="form-label">Başlık</label>
                              <input
                                name="title"
                                className="form-control"
                                value={requestForm.title}
                                onChange={handleRequestChange}
                                required
                              />
                            </div>

                            <div className="row">
                              <div className="col-md-6 mb-2">
                                <label className="form-label">Yükleme</label>
                                <input
                                  name="routeFrom"
                                  className="form-control"
                                  value={requestForm.routeFrom}
                                  onChange={handleRequestChange}
                                  required
                                />
                              </div>

                              <div className="col-md-6 mb-2">
                                <label className="form-label">Varış</label>
                                <input
                                  name="routeTo"
                                  className="form-control"
                                  value={requestForm.routeTo}
                                  onChange={handleRequestChange}
                                  required
                                />
                              </div>
                            </div>

                            <div className="mb-2">
                              <label className="form-label">Yük Tipi</label>
                              <input
                                name="loadType"
                                className="form-control"
                                value={requestForm.loadType}
                                onChange={handleRequestChange}
                              />
                            </div>

                            <div className="row">
                              <div className="col-md-6 mb-2">
                                <label className="form-label">Miktar</label>
                                <input
                                  name="quantity"
                                  type="number"
                                  className="form-control"
                                  value={requestForm.quantity}
                                  onChange={handleRequestChange}
                                />
                              </div>

                              <div className="col-md-6 mb-2">
                                <label className="form-label">Birim</label>
                                <input
                                  name="quantityUnit"
                                  className="form-control"
                                  value={requestForm.quantityUnit}
                                  onChange={handleRequestChange}
                                />
                              </div>
                            </div>

                            <div className="mb-2">
                              <label className="form-label">Araç Tipi</label>
                              <input
                                name="vehicleType"
                                className="form-control"
                                value={requestForm.vehicleType}
                                onChange={handleRequestChange}
                              />
                            </div>

                            <div className="mb-2">
                              <label className="form-label">
                                Son Teklif Tarihi
                              </label>
                              <input
                                name="dueDate"
                                type="date"
                                className="form-control"
                                value={requestForm.dueDate}
                                onChange={handleRequestChange}
                                required
                              />
                            </div>

                            <div className="mb-2">
                              <label className="form-label">Açıklama</label>
                              <textarea
                                name="description"
                                className="form-control"
                                rows="3"
                                value={requestForm.description}
                                onChange={handleRequestChange}
                              />
                            </div>

                            <button
                              type="submit"
                              className="btn btn-primary"
                              disabled={loading}
                            >
                              Teklif Talebi Oluştur
                            </button>
                          </form>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-8">
                      <div className="table-responsive">
                        <table className="table table-hover table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Kod</th>
                              <th>Başlık</th>
                              <th>Rota</th>
                              <th>Son Tarih</th>
                              <th>Firma</th>
                              <th>Cevap</th>
                              <th>Eşleşen</th>
                              <th>Eşleşmeyen</th>
                              <th>Durum</th>
                              <th>Form</th>
                              <th>İşlem</th>
                            </tr>
                          </thead>

                          <tbody>
                            {requests.length > 0 ? (
                              requests.map((item) => {
                                const stats = item.offerStats || {};
                                const recipients =
                                  getRequestMailRecipients(item);

                                return (
                                  <tr key={item._id}>
                                    <td className="fw-bold">
                                      {item.requestCode}
                                    </td>
                                    <td>{item.title}</td>
                                    <td>
                                      {item.routeFrom} → {item.routeTo}
                                    </td>
                                    <td>{formatDate(item.dueDate)}</td>
                                    <td>{stats.selectedCompanyCount || 0}</td>
                                    <td>
                                      <span className="badge bg-primary">
                                        {stats.totalOfferCount || 0}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="badge bg-success">
                                        {stats.submittedCount || 0}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="badge bg-warning">
                                        {stats.unmatchedCount || 0}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="badge bg-info">
                                        {item.status}
                                      </span>
                                    </td>
                                    <td>
                                      {item.googleResponderUrl ? (
                                        <div className="d-flex flex-column gap-1">
                                          <a
                                            href={item.googleResponderUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn btn-sm btn-outline-success"
                                          >
                                            Formu Aç
                                          </a>

                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() =>
                                              handleCopyLink(
                                                item.googleResponderUrl
                                              )
                                            }
                                          >
                                            Link Kopyala
                                          </button>
                                        </div>
                                      ) : (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-warning"
                                          onClick={() =>
                                            handleCreateGoogleForm(item._id)
                                          }
                                        >
                                          Form Oluştur
                                        </button>
                                      )}
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary me-1 mb-1"
                                        onClick={() => goDetail(item._id)}
                                      >
                                        Detay
                                      </button>

                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-dark me-1 mb-1"
                                        onClick={() => handleSendMail(item)}
                                        disabled={
                                          !item.googleFormId ||
                                          !item.googleResponderUrl ||
                                          recipients.length === 0
                                        }
                                      >
                                        Mail Gönder
                                      </button>

                                      <button
                                        type="button"
                                        className="btn btn-sm btn-success mb-1"
                                        onClick={() =>
                                          handleSyncResponses(item._id)
                                        }
                                        disabled={!item.googleFormId}
                                      >
                                        Cevapları Çek
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="11" className="text-center p-4">
                                  Kayıt bulunamadı.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="alert alert-secondary mt-3 mb-0">
                        <strong>Not:</strong> Cevaplar her teklif talebine ayrı
                        bağlıdır. Detay ekranında yalnızca seçilen talebin
                        cevapları görüntülenir. Mail gönderiminde seçili
                        firmalar ayrıca seçilmez; talep oluşturulurken otomatik
                        eklenen aktif ve izinli firmalara gönderilir. Form
                        cevaplarında navlun bedeli, line, freetime ve teslim TT
                        bilgileri takip edilir.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}

export default DirectorNavlunRequestPage;
